import { BoxProps } from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { Box } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import React, { FunctionComponent } from "react";
import { BackgroundColorHandlerFunction, TRANSPARENT, TransparentSwatch, cssCheckeredBackground } from "./ColorPicker";

export interface DefaultColorsProps {
	/** the currently selected color. */
	color: string;

	/** the initial color to be shown as selected by the picker, in hex. */
	colors: string[];

	/** the callback for when the user selects a color. */
	onColorChange: BackgroundColorHandlerFunction;
}

export interface ColorSwatchProps extends BoxProps {
	/** the displayed color. */
	color: string;

	/** the callback for when the user selects a color. */
	onColorChange: BackgroundColorHandlerFunction;

	/** whether the swatch is currently selected. */
	selected?: boolean;

	/** the border color used when the swatch is currently selected. */
	selectionBorderColor: string;
}

/**
 * generate title for color swatch colors
 */
const generateTitle = (swatchColor: string): string => {
	const loc = useLocale();
	let title;
	switch (swatchColor) {
		case TRANSPARENT:
			title = loc.resource("ColorNoFill");
			break;
		default:
			title = swatchColor;
			break;
	}

	return title;
};

export const ColorSwatch: FunctionComponent<ColorSwatchProps> = (props) => {
	const {
		color: swatchColor,
		onColorChange,
		selected,
		selectionBorderColor,
		sx: extraSx,
		title,
	} = props;

	return (
		<Box
			onClick={() => onColorChange(swatchColor)}
			sx={{
				background: (swatchColor === TRANSPARENT) ? "#FFF" : `linear-gradient(${swatchColor}, ${swatchColor}), ${cssCheckeredBackground}`,
				border: "1px solid rgba(0, 0, 0, 0.1)",
				borderRadius: "50%",
				boxShadow: (selected) ? `0 0 0px 2px ${selectionBorderColor}` : null,
				cursor: "pointer",
				height: "20px",
				// Firefox 107 fix: We had to add 2px on the top and bottom margins so the box-shadow isn't cut off.
				// This behavior is not exhibited in other browsers.
				margin: "6px 4px",
				position: "relative",
				width: "20px",
				...extraSx,
			}}
			title={title}
		>
			{(swatchColor === TRANSPARENT) && <TransparentSwatch />}
		</Box>
	);
};

const DefaultColors: FunctionComponent<DefaultColorsProps> = (props) => {
	const { color, colors = [], onColorChange } = props;
	const theme = useTheme();

	return (
		<Box
			sx={{
				display: "flex",
				flexWrap: "wrap",
				width: "224px",
			}}
		>
			{colors.map((swatchColor, index) => (
				<ColorSwatch
					color={swatchColor}
					key={index}
					onColorChange={onColorChange}
					selected={swatchColor === color}
					selectionBorderColor={theme.palette.FocusPrimary}
					title={generateTitle(swatchColor)}
				/>
			))}
		</Box>
	);
};

export default DefaultColors;
